import { create } from 'zustand';
import dayjs, { Dayjs } from 'dayjs';
import API from '../api2';

// Core filter types
export interface WHLocationFilter { id: string; locationName: string; customer?: string; }
export interface WHFilters {
	sheetTypes: string[];
	dateFrom: Dayjs | null;
	dateTo: Dayjs | null;
	customers: string[];
	locations: WHLocationFilter[];
	sheetId: string | null; // direct lookup
}

export interface WHResultRecord {
	id: string;
	sheetType: string; // key matching table map
	serviceDate: string | null;
	locationId?: string | number | null;
	locationName?: string | null;
	summary?: string | null;
	data: any; // raw row for PDF
}

interface WHState {
	filters: WHFilters;
	loading: boolean;
	results: WHResultRecord[];
	selected: WHResultRecord | null;
	// setters
	setSheetTypes: (v: string[]) => void;
	setDateFrom: (d: Dayjs | null) => void;
	setDateTo: (d: Dayjs | null) => void;
	setCustomers: (arr: string[]) => void;
	setLocations: (arr: WHLocationFilter[]) => void;
	setSheetId: (v: string | null) => void;
	patchFilters: (partial: Partial<WHFilters>) => void;
	clearAdvanced: () => void;
	// results
	setResults: (r: WHResultRecord[]) => void;
	setSelected: (r: WHResultRecord | null) => void;
	runSearch: (opts?: { customers?: string[]; locations?: WHLocationFilter[] }) => Promise<void>;
}

const defaultFilters: WHFilters = {
	sheetTypes: [],
	dateFrom: dayjs().subtract(30, 'day'),
	dateTo: dayjs(),
	customers: [],
	locations: [],
	sheetId: null
};

export const useWorkHistoryStore = create<WHState>((set, get) => ({
	filters: defaultFilters,
	loading: false,
	results: [],
	selected: null,

	setSheetTypes: (v) => set(s => ({ filters: { ...s.filters, sheetTypes: v } })),
	setDateFrom: (d) => set(s => ({ filters: { ...s.filters, dateFrom: d } })),
	setDateTo: (d) => set(s => ({ filters: { ...s.filters, dateTo: d } })),
	setCustomers: (arr) => set(s => ({ filters: { ...s.filters, customers: arr } })),
	setLocations: (arr) => set(s => ({ filters: { ...s.filters, locations: arr } })),
	setSheetId: (v) => set(s => ({ filters: { ...s.filters, sheetId: v } })),
	patchFilters: (partial) => set(s => ({ filters: { ...s.filters, ...partial } })),
	clearAdvanced: () => set(s => ({ filters: { ...s.filters, customers: [], locations: [], sheetId: null } })),

	setResults: (r) => set({ results: r }),
	setSelected: (r) => set(s => ({ selected: s.selected?.id === r?.id ? null : r })),

	runSearch: async (opts) => {
		const { filters } = get();
		const { sheetTypes, dateFrom, dateTo, customers, locations, sheetId } = filters;
		const fallbackCustomers = (!customers.length && opts?.customers?.length) ? opts.customers : customers;
		const fallbackLocations = (!locations.length && opts?.locations?.length) ? opts.locations : locations;
		if (!sheetId && sheetTypes.length === 0) {
			console.warn('[WorkHistory] Must select at least one sheet type or provide a Sheet ID');
			return;
		}

		const payload = {
			sheetTypes: sheetTypes,
			dateFrom: dateFrom ? dateFrom.startOf('day').toISOString() : null,
			dateTo: dateTo ? dateTo.endOf('day').toISOString() : null,
			customers: fallbackCustomers,
			locations: fallbackLocations.map(l => ({ id: l.id, locationName: l.locationName })), 
			sheetId: sheetId || undefined
		};
		set({ loading: true });
		try {
			const response = await API.getSheetsHistory(payload);
			const collected: WHResultRecord[] = [];
			if (response && response.data) {
				Object.entries<any>(response.data).forEach(([typeKey, arr]) => {
					if (!Array.isArray(arr)) return;
						arr.forEach(rec => collected.push({
							id: String(rec.id),
							sheetType: typeKey,
							serviceDate: rec.serviceDate || rec.dateServiced || null,
							locationId: rec.locationId,
							locationName: rec.locationName,
							summary: rec.summary || null,
							data: rec.pdfData || rec
						}));
				});
			}
			// Sort by serviceDate desc
			collected.sort((a,b) => {
				const da = a.serviceDate ? new Date(a.serviceDate).getTime() : -Infinity;
				const db = b.serviceDate ? new Date(b.serviceDate).getTime() : -Infinity;
				return db - da;
			});
			set({ results: collected });
		} catch (e) {
			console.error('[WorkHistory] API error', e);
			set({ results: [] });
		} finally {
			set({ loading: false });
		}
	}
}));

export default useWorkHistoryStore;
